import {
  type ITransformRT3,
  type IVector2,
  type IVector3,
  Matrix3Ns,
  QuaternionNs,
  Vector2,
  Vector3,
} from '@seddi/math-v2';
import {
  type Button,
  GarmentPieceButtonElements,
  type IGarmentPieceButton,
  type IScene,
  type ISceneEntity,
} from '@seddi/scene';
import * as SceneModule from './sceneModule';

interface TriBarycentric {
  /** 1st vertex index. */
  a: number;
  /** 2nd vertex index. */
  b: number;
  /** 3rd vertex index. */
  c: number;
  /** 1st barycentric coordinate. */
  x: number;
  /** 2nd barycentric coordinate. */
  y: number;
  /** 3rd barycentric coordinate. */
  z: number;
}

const CELL_SIZE = 0.015;

function interpolate3(barycentric: TriBarycentric, data: Float32Array): Vector3 {
  const { a, b, c, x, y, z } = barycentric;
  const o0 = a * 3;
  const o1 = b * 3;
  const o2 = c * 3;
  const v0 = { x: data[o0], y: data[o0 + 1], z: data[o0 + 2] };
  const v1 = { x: data[o1], y: data[o1 + 1], z: data[o1 + 2] };
  const v2 = { x: data[o2], y: data[o2 + 1], z: data[o2 + 2] };
  const ret = new Vector3(
    v0.x * x + v1.x * y + v2.x * z,
    v0.y * x + v1.y * y + v2.y * z,
    v0.z * x + v1.z * y + v2.z * z,
  );
  return ret;
}

function queryBarycentric(entity: ISceneEntity, point: IVector2): TriBarycentric {
  // update query acceleration structures
  const pieceExt = SceneModule.sceneExt.addPiece(entity._id);
  const { indices, position2D } = entity.components.garmentPiece!.geometry;
  pieceExt!.updateGrid(indices, position2D, CELL_SIZE);

  const { coordinates, triangle } = pieceExt!.grid.query(point);
  const offset = triangle * 3;
  const a = indices[offset];
  const b = indices[offset + 1];
  const c = indices[offset + 2];
  const { x, y, z } = coordinates;
  const ret = { a, b, c, x, y, z };
  return ret;
}

function computeTangent(
  barycentric: TriBarycentric,
  pos: Float32Array,
  uvs: Float32Array,
): Vector3 {
  const { a, b, c } = barycentric;
  const o20 = a * 2;
  const o21 = b * 2;
  const o22 = c * 2;
  const o30 = a * 3;
  const o31 = b * 3;
  const o32 = c * 3;
  const pos0 = new Vector3(pos[o30], pos[o30 + 1], pos[o30 + 2]);
  const pos1 = new Vector3(pos[o31], pos[o31 + 1], pos[o31 + 2]);
  const pos2 = new Vector3(pos[o32], pos[o32 + 1], pos[o32 + 2]);
  const uv0 = new Vector2(uvs[o20], uvs[o20 + 1]);
  const uv1 = new Vector2(uvs[o21], uvs[o21 + 1]);
  const uv2 = new Vector2(uvs[o22], uvs[o22 + 1]);
  const edge1 = new Vector3();
  const edge2 = new Vector3();
  const delta1 = new Vector2();
  const delta2 = new Vector2();
  pos1.subtractToRef(pos0, edge1);
  pos2.subtractToRef(pos0, edge2);
  uv1.subtractToRef(uv0, delta1);
  uv2.subtractToRef(uv0, delta2);

  const f = 1.0 / (delta1.x * delta2.y - delta2.x * delta1.y || 1);
  const x = f * (delta2.y * edge1.x - delta1.y * edge2.x);
  const y = f * (delta2.y * edge1.y - delta1.y * edge2.y);
  const z = f * (delta2.y * edge1.z - delta1.y * edge2.z);

  const result = new Vector3(x, y, z);
  result.normalize();
  return result;
}

function isClosedButton(buttonPosition: IVector3, holePosition: IVector3): boolean {
  const OVERLAP_DISTANCE = 0.04;
  return Vector3.Distance(buttonPosition, holePosition) <= OVERLAP_DISTANCE;
}

function computeTransform(
  ebutton: IGarmentPieceButton,
  entity: ISceneEntity,
  button: Button,
  scene: IScene,
): ITransformRT3 {
  let bary = queryBarycentric(entity, ebutton.transform.translation);
  let geometry = entity.components.garmentPiece!.geometry;
  let position = interpolate3(bary, geometry.position3D);

  // If the button is closed we place the button on top of the hole
  const { pair } = ebutton;
  if (pair) {
    const hole = GarmentPieceButtonElements.FindButtonPairInScene(scene, pair)!;
    const { entity: holeEntity, buttonHole: ehole } = hole;
    const holeBary = queryBarycentric(holeEntity, ehole!.transform.translation);
    const holeGeometry = holeEntity.components.garmentPiece!.geometry;
    const holePosition = interpolate3(holeBary, holeGeometry.position3D);

    if (isClosedButton(position, holePosition)) {
      bary = holeBary;
      // eslint-disable-next-line no-param-reassign -- Todo
      entity = holeEntity;
      geometry = holeGeometry;
      position = holePosition;
    }
  }

  const pieceExt = SceneModule.sceneExt.addPiece(entity._id);
  pieceExt!.updateNormals(geometry.indices, geometry.position3D);
  const normal = interpolate3(bary, pieceExt!.normals);
  normal.normalize();
  const tangent = computeTangent(bary, geometry.position3D, geometry.position2D);
  const bitangent = Vector3.Cross(normal, tangent);
  const m = Matrix3Ns.fromBasis(tangent, bitangent, normal);
  const q = QuaternionNs.fromRotationMatrix(m);
  const h = button.bounds.height / 2;

  const ret: ITransformRT3 = {
    translation: {
      x: position.x + normal.x * h,
      y: position.y + normal.y * h,
      z: position.z + normal.z * h,
    },
    rotation: { x: q.x, y: q.y, z: q.z, w: q.w },
  };

  return ret;
}

export const PieceButton = {
  computeTransform,
};
